import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import PatientRegister from './components/PatientRegister';
import ClinicianDashboard from './components/ClinicianDashboard';
import PatientDashboard from './components/PatientDashboard';
import PatientProfilePage from './components/PatientProfilePage';
import SurveyResponseDetail from './components/SurveyResponseDetail';
// Debug components - uncomment if needed for development
// import DebugFirebase from './components/DebugFirebase';
// import DebugFirestore from './components/DebugFirestore';
// import DebugStatus from './components/DebugStatus';
import './App.css';

// Debug logging
console.log('App.js: Starting application initialization');

// Protected Route component - FIXED VERSION
const ProtectedRoute = ({ children, allowedUserType, path }) => {
  // Move useAuth() INSIDE the component function
  const { currentUser, userType } = useAuth();
  
  console.log('🔒 ProtectedRoute:', { 
    path: window.location.pathname,
    allowedUserType,
    hasCurrentUser: !!currentUser,
    userType: userType
  });
  
  if (!currentUser) {
    console.log('🔒 ProtectedRoute: No current user, redirecting to login');
    return <Navigate to="/login" replace state={{ from: path || '/' }} />;
  }
  
  if (allowedUserType && userType !== allowedUserType) {
    console.log(`🔒 ProtectedRoute: User type mismatch. Expected ${allowedUserType}, got ${userType}, redirecting to /`);
    return <Navigate to="/" replace />;
  }
  
  console.log('🔒 ProtectedRoute: Access granted');
  return children;
};

// Main App Routes
const AppRoutes = () => {
  const { currentUser, userType } = useAuth();
  
  useEffect(() => {
    console.log('AppRoutes: Current user state changed', { 
      hasUser: !!currentUser, 
      userType,
      currentUser: currentUser ? { uid: currentUser.uid, email: currentUser.email } : null
    });
  }, [currentUser, userType]);
  
  return (
    <Routes>
      <Route 
        path="/register" 
        element={currentUser ? <Navigate to="/" replace /> : <PatientRegister />} 
      />
      <Route 
        path="/login" 
        element={currentUser ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute path="/">
            {userType === 'clinician' ? <ClinicianDashboard /> : <PatientDashboard />}
          </ProtectedRoute>
        } 
      />
      {/* Redirect /clinician to /clinician/patients */}
      <Route 
        path="/clinician" 
        element={
          <ProtectedRoute path="/clinician" allowedUserType="clinician">
            <Navigate to="/clinician/patients" replace />
          </ProtectedRoute>
        } 
      />
      
      {/* Main clinician dashboard routes - order is important */}
      <Route 
        path="/clinician/patients/:patientId" 
        element={
          <ProtectedRoute path="/clinician/patients/:patientId" allowedUserType="clinician">
            <PatientProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/patients" 
        element={
          <ProtectedRoute path="/clinician/patients" allowedUserType="clinician">
            <ClinicianDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/:section" 
        element={
          <ProtectedRoute path="/clinician/:section" allowedUserType="clinician">
            <ClinicianDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/*" 
        element={
          <ProtectedRoute path="/patient" allowedUserType="patient">
            <PatientDashboard />
          </ProtectedRoute>
        } 
      />
      {/* Survey response detail page with navigation */}
      <Route 
        path="/survey-response/:responseId" 
        element={
          <ProtectedRoute allowedUserType="clinician">
            <ClinicianDashboard>
              <SurveyResponseDetail />
            </ClinicianDashboard>
          </ProtectedRoute>
        } 
      />
      
      {/* Debug routes - uncomment if needed for development */}
      {/*
      <Route 
        path="/debug-firebase" 
        element={<DebugFirebase />} 
      />
      <Route 
        path="/debug-firestore" 
        element={<DebugFirestore />} 
      />
      */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen pb-16">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;